import React, { useState, useEffect } from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Plus, Edit, ChevronLeft, Search } from 'lucide-react';
import { RecordActions } from '../../../components/common/RecordActions';
import { RecordViewDialog } from '../../../components/common/RecordViewDialog';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { psAPI } from '../../../services/api';
import { toast } from 'sonner';

const PlanningProfiles = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('planningprofiles');
  const [viewItem, setViewItem] = useState(null);
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [formData, setFormData] = useState({
    profile_id: '', name: '', description: '', planning_method: 'MANUAL',
    allow_multiple_versions: true, default_version: '0', plan_by_cost_element: true,
    planning_period_type: 'MONTHLY', integrate_with_budget: true, transfer_to_commitment: false, status: 'Active'
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await psAPI.getPlanningProfiles();
      setProfiles(response.data);
    } catch (error) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const handleCreate = () => {
    setEditMode(false);
    setFormData({ profile_id: '', name: '', description: '', planning_method: 'MANUAL', allow_multiple_versions: true, default_version: '0', plan_by_cost_element: true, planning_period_type: 'MONTHLY', integrate_with_budget: true, transfer_to_commitment: false, status: 'Active' });
    setShowDialog(true);
  };

  const handleEdit = (profile) => {
    setEditMode(true);
    setFormData({ ...profile, description: profile.description || '' });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.profile_id || !formData.name) { toast.error('Fill required fields'); return; }
      if (editMode) { await psAPI.updatePlanningProfile(formData.profile_id, formData); toast.success('Updated'); }
      else { await psAPI.createPlanningProfile(formData); toast.success('Created'); }
      setShowDialog(false); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const filteredProfiles = profiles.filter(p => p.profile_id.toLowerCase().includes(searchTerm.toLowerCase()) || p.name.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <Layout>
    <div className="p-6 space-y-6" data-testid="planning-profiles-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/ps')}><ChevronLeft className="h-5 w-5" /></Button>
        <div className="flex-1"><h1 className="text-2xl font-bold">Planning Profiles</h1><p className="text-gray-500">Configure cost planning methods and settings</p></div>
        {writable && (<Button onClick={handleCreate}><Plus className="h-4 w-4 mr-2" />New Profile</Button>)}
      </div>
      <Card><CardContent className="pt-6"><div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" /><Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" /></div></CardContent></Card>
      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>Profile ID</TableHead><TableHead>Name</TableHead><TableHead>Planning Method</TableHead><TableHead>Period Type</TableHead><TableHead>Budget Integration</TableHead><TableHead>Status</TableHead><TableHead className="w-[80px]">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={7} className="text-center py-8">Loading...</TableCell></TableRow> :
            filteredProfiles.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">No profiles</TableCell></TableRow> :
            filteredProfiles.map((p) => (
              <TableRow key={p.profile_id}>
                <TableCell className="font-medium">{p.profile_id}</TableCell>
                <TableCell>{p.name}</TableCell>
                <TableCell><Badge variant="outline">{p.planning_method}</Badge></TableCell>
                <TableCell>{p.planning_period_type}</TableCell>
                <TableCell><Badge variant={p.integrate_with_budget ? 'default' : 'secondary'}>{p.integrate_with_budget ? 'Yes' : 'No'}</Badge></TableCell>
                <TableCell><Badge variant={p.status === 'Active' ? 'default' : 'secondary'}>{p.status}</Badge></TableCell>
                <TableCell><RecordActions doctype="planningprofiles" onView={() => setViewItem(p)} onEdit={() => handleEdit(p)} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editMode ? 'Edit' : 'Create'} Planning Profile</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2"><Label>Profile ID *</Label><Input value={formData.profile_id} onChange={(e) => setFormData({...formData, profile_id: e.target.value.toUpperCase()})} disabled={editMode} /></div>
            <div className="space-y-2"><Label>Name *</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} /></div>
            <div className="col-span-2 space-y-2"><Label>Description</Label><Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} /></div>
            <div className="space-y-2"><Label>Planning Method</Label><Select value={formData.planning_method} onValueChange={(v) => setFormData({...formData, planning_method: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="MANUAL">Manual</SelectItem><SelectItem value="UNIT_COSTING">Unit Costing</SelectItem><SelectItem value="ECP">Easy Cost Planning</SelectItem><SelectItem value="NETWORK_COSTING">Network Costing</SelectItem></SelectContent></Select></div>
            <div className="space-y-2"><Label>Period Type</Label><Select value={formData.planning_period_type} onValueChange={(v) => setFormData({...formData, planning_period_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="MONTHLY">Monthly</SelectItem><SelectItem value="QUARTERLY">Quarterly</SelectItem><SelectItem value="YEARLY">Yearly</SelectItem></SelectContent></Select></div>
            <div className="space-y-2"><Label>Default Version</Label><Input value={formData.default_version} onChange={(e) => setFormData({...formData, default_version: e.target.value})} /></div>
            <div className="space-y-2"><Label>Status</Label><Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem></SelectContent></Select></div>
            <div className="col-span-2 grid grid-cols-2 gap-4 pt-4 border-t">
              <div className="flex items-center justify-between"><Label>Multiple Versions</Label><Switch checked={formData.allow_multiple_versions} onCheckedChange={(v) => setFormData({...formData, allow_multiple_versions: v})} /></div>
              <div className="flex items-center justify-between"><Label>By Cost Element</Label><Switch checked={formData.plan_by_cost_element} onCheckedChange={(v) => setFormData({...formData, plan_by_cost_element: v})} /></div>
              <div className="flex items-center justify-between"><Label>Budget Integration</Label><Switch checked={formData.integrate_with_budget} onCheckedChange={(v) => setFormData({...formData, integrate_with_budget: v})} /></div>
              <div className="flex items-center justify-between"><Label>Transfer to Commitment</Label><Switch checked={formData.transfer_to_commitment} onCheckedChange={(v) => setFormData({...formData, transfer_to_commitment: v})} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button><Button onClick={handleSubmit}>{editMode ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default PlanningProfiles;
